import { ref } from 'vue';
import axios from '@/plugins/axios';
import { useStorage } from '@/composables/use-storage';

export function useEvalRunsQueue() {
  const { userAuthToken } = useStorage();
  
  const loading = ref(false);
  const queueHealth = ref(null);
  const jobCounts = ref(null);
  const recentActivity = ref(null);
  const asymmConfig = ref(null);

  const getAuthHeaders = () => {
    const token = userAuthToken?.value;
    if (!token) {
      // Try to get token directly from localStorage as fallback
      const fallbackToken = localStorage.getItem('authToken');
      if (!fallbackToken) {
        console.warn('No authentication token available, skipping API call');
        return null;
      }
      return {
        'Authorization': `Bearer ${fallbackToken}`
      };
    }
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  const makeApiCall = async (method, url, data = null) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        console.warn('Skipping API call due to missing authentication token');
        return null;
      }

      const config = {
        method,
        url: `/api/eval-runs-queue${url}`,
        headers
      };
      
      if (data) {
        config.data = data;
      }
      
      return await axios(config);
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  };

  const showNotification = (message, type = 'info') => {
    // You can integrate with your notification system here
    console.log(`${type.toUpperCase()}: ${message}`);
  };

  const waitForAuthToken = async (maxRetries = 10, delay = 500) => {
    for (let i = 0; i < maxRetries; i++) {
      const token = userAuthToken?.value || localStorage.getItem('authToken');
      if (token) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    return false;
  };

  const refreshData = async () => {
    loading.value = true;
    try {
      // Wait for auth token to be available
      const hasToken = await waitForAuthToken();
      if (!hasToken) {
        console.warn('Authentication token not available after waiting, skipping data refresh');
        return;
      }

      await Promise.all([
        fetchQueueHealth(),
        fetchJobCounts(),
        fetchRecentActivity(),
        fetchAsymmConfig()
      ]);
      showNotification('Data refreshed successfully', 'success');
    } catch (error) {
      console.error('Error refreshing data:', error);
      showNotification('Failed to refresh data', 'error');
    } finally {
      loading.value = false;
    }
  };

  const fetchQueueHealth = async () => {
    try {
      const response = await makeApiCall('GET', '/health');
      if (response) {
        queueHealth.value = response.data;
      }
    } catch (error) {
      console.error('Error fetching queue health:', error);
      // Don't throw error to prevent breaking the UI
    }
  };

  const fetchJobCounts = async () => {
    try {
      const response = await makeApiCall('GET', '/counts');
      if (response) {
        jobCounts.value = response.data;
      }
    } catch (error) {
      console.error('Error fetching job counts:', error);
      // Don't throw error to prevent breaking the UI
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await makeApiCall('GET', '/activity?hours=24');
      if (response) {
        recentActivity.value = response.data;
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      // Don't throw error to prevent breaking the UI
    }
  };

  const fetchAsymmConfig = async () => {
    try {
      const response = await makeApiCall('GET', '/test-asymm-config');
      if (response) {
        console.log('Asymm config response:', response.data);
        asymmConfig.value = response.data;
      }
    } catch (error) { 
      console.error('Error fetching Asymm config:', error);
      // Don't throw error to prevent breaking the UI
    }
  };

  const pauseQueue = async () => {
    loading.value = true;
    try {
      const response = await makeApiCall('POST', '/pause');
      if (response) {
        showNotification(response.data.message || 'Queue paused successfully', 'success');
        await fetchQueueHealth();
      } else {
        showNotification('Authentication required to pause queue', 'warning');
      }
    } catch (error) {
      console.error('Error pausing queue:', error);
      showNotification('Failed to pause queue', 'error');
    } finally {
      loading.value = false;
    }
  };

  const resumeQueue = async () => {
    loading.value = true;
    try {
      const response = await makeApiCall('POST', '/resume');
      if (response) {
        showNotification(response.data.message || 'Queue resumed successfully', 'success');
        await fetchQueueHealth();
      } else {
        showNotification('Authentication required to resume queue', 'warning');
      }
    } catch (error) {
      console.error('Error resuming queue:', error);
      showNotification('Failed to resume queue', 'error');
    } finally {
      loading.value = false;
    }
  };

  const retryFailedJobs = async () => {
    loading.value = true;
    try {
      const response = await makeApiCall('POST', '/retry-failed');
      if (response) {
        showNotification(response.data.message || 'Failed jobs retried successfully', 'success');
        await Promise.all([fetchJobCounts(), fetchRecentActivity()]);
      } else {
        showNotification('Authentication required to retry failed jobs', 'warning');
      }
    } catch (error) {
      console.error('Error retrying failed jobs:', error);
      showNotification('Failed to retry failed jobs', 'error');
    } finally {
      loading.value = false;
    }
  };

  const clearAllJobs = async () => {
    if (!confirm('Are you sure you want to clear all jobs? This action cannot be undone.')) {
      return;
    }
    
    loading.value = true;
    try {
      const response = await makeApiCall('POST', '/clear-all');
      if (response) {
        showNotification(response.data.message || 'All jobs cleared successfully', 'success');
        await Promise.all([fetchJobCounts(), fetchRecentActivity()]);
      } else {
        showNotification('Authentication required to clear all jobs', 'warning');
      }
    } catch (error) {
      console.error('Error clearing all jobs:', error);
      showNotification('Failed to clear all jobs', 'error');
    } finally {
      loading.value = false;
    }
  };

  const testAsymmConfig = async () => {
    loading.value = true;
    try {
      const response = await makeApiCall('GET', '/test-asymm-config');
      if (response) {
        asymmConfig.value = response.data;
        showNotification('Asymm configuration test completed', 'success');
      } else {
        showNotification('Authentication required to test Asymm configuration', 'warning');
      }
    } catch (error) {
      console.error('Error testing Asymm config:', error);
      showNotification('Failed to test Asymm configuration', 'error');
    } finally {
      loading.value = false;
    }
  };

  const getJobsByStatus = async (status, limit = 10) => {
    try {
      const response = await makeApiCall('GET', `/jobs/${status}?limit=${limit}`);
      return response ? response.data : null;
    } catch (error) {
      console.error(`Error fetching jobs by status ${status}:`, error);
      return null;
    }
  };

  const getJobDetails = async (jobId) => {
    try {
      const response = await makeApiCall('GET', `/jobs/detail/${jobId}`);
      return response ? response.data : null;
    } catch (error) {
      console.error(`Error fetching job details for ${jobId}:`, error);
      return null;
    }
  };

  const getJobsByType = async (jobType, limit = 10) => {
    try {
      const response = await makeApiCall('GET', `/jobs/type/${jobType}?limit=${limit}`);
      return response ? response.data : null;
    } catch (error) {
      console.error(`Error fetching jobs by type ${jobType}:`, error);
      return null;
    }
  };

  const clearQueue = async (jobTypes = []) => {
    loading.value = true;
    try {
      const response = await makeApiCall('POST', '/clear', { jobTypes });
      if (response) {
        showNotification(response.data.message || 'Queue cleared successfully', 'success');
        await fetchJobCounts();
      } else {
        showNotification('Authentication required to clear queue', 'warning');
      }
    } catch (error) {
      console.error('Error clearing queue:', error);
      showNotification('Failed to clear queue', 'error');
    } finally {
      loading.value = false;
    }
  };

  return {
    loading,
    queueHealth,
    jobCounts,
    recentActivity,
    asymmConfig,
    refreshData,
    pauseQueue,
    resumeQueue,
    retryFailedJobs,
    clearAllJobs,
    testAsymmConfig,
    getJobsByStatus,
    getJobDetails,
    getJobsByType,
    clearQueue
  };
}
